/* eslint-disable */
// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from 'react';
import { type PlaybackDiagnostic, useAudioPlayback } from './useAudioPlayback';
import getToken from './getToken';
import {
  INTERVIEW_SEGMENTS,
  type InterviewSegment,
} from './segments';
import { TOTAL_TIME_MS } from './enums';

// Tab-wide singleton to prevent multiple concurrent WebSocket connections
type InterviewSocketState = {
  ws: WebSocket | null;
  connecting: boolean;
};

const INTERVIEW_SOCKET_STATE_KEY = '__interviewSocketState' as const;

const ENABLE_INTERVIEW_DEBUG = process.env.REACT_APP_DEBUG_INTERVIEW === 'true';

// Ensure global state exists once per tab without using underscore identifiers
const ensureGlobalState = (): InterviewSocketState | null => {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as Record<string, unknown>;
  if (!w[INTERVIEW_SOCKET_STATE_KEY]) {
    w[INTERVIEW_SOCKET_STATE_KEY] = {
      ws: null,
      connecting: false,
    } as InterviewSocketState;
  }
  return w[INTERVIEW_SOCKET_STATE_KEY] as InterviewSocketState;
};

const getState = (): InterviewSocketState => {
  const state = ensureGlobalState();
  return state ?? { ws: null, connecting: false };
};

// Prefer API Gateway for admit to avoid ALB 503s when tasks are draining or 0 healthy
const ADMIT_API_BASE =
  process.env.REACT_APP_AI_INTERVIEW_AUTOSCALE ||
  'https://ynawcv1cc8.execute-api.us-west-2.amazonaws.com';

const VALID_SEGMENT_SET = new Set<InterviewSegment>(INTERVIEW_SEGMENTS);

export interface InterviewMessage {
  type: 'transcript' | 'assistant' | 'error' | 'connection' | 'question';
  data: string;
  messageId?: string;
  sequence?: number;
  timestamp?: number;
}

export interface EndInterviewButtonHint {
  target: 'end_interview_button';
  action: 'highlight';
  reason?: string;
  durationMs?: number;
  message?: string;
  nonce: number;
}

interface WebSocketMessageData {
  type: string;
  content?: string;
  data?: string;
  messageId?: string;
  id?: string;
  turnId?: string;
  sequence?: number;
  audio?: string;
  message?: string;
  partial?: boolean;
  delta?: boolean;
  // Phantom feedback loop properties
  action?: string;
  consecutivePhantoms?: number;
}

interface StartInterviewConfig {
  sessionId?: string;
  interviewType?: string;
  difficultyLevel?: string;
  documentId?: string;
  resumeId?: string;
}

interface SendMessageData {
  type?: string;
  t?: string;
  d?: string | null; // Data field for ai-interview protocol
  config?: StartInterviewConfig;
  text?: string;
  audio?: string;
  timestamp: number;
}

// Debounce utility to prevent too frequent message buffer flushes
const debounce = <T extends (...args: unknown[]) => void>(
  fn: T,
  ms: number,
) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return function debouncedFunction(this: unknown, ...args: Parameters<T>) {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

const debugLog = (...args: unknown[]) => {
  if (ENABLE_INTERVIEW_DEBUG) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

const ADMIT_RETRY_TIMEOUT_MS = 90 * 1000;
const ADMIT_RETRY_MAX_DELAY_MS = 3000;
const FIRST_ASSISTANT_AUDIO_SYNC_GATE_MS = 150;

export const useInterviewSocket = (
  wsUrl?: string,
  options?: {
    fallbackSegments?: InterviewSegment[];
    fallbackTotalMs?: number;
    refreshWsUrl?: () => Promise<string | null | undefined>;
  },
) => {
  const wsRef = useRef<WebSocket | null>(null);
  const refreshWsUrlRef = useRef(options?.refreshWsUrl);
  const [isConnected, setIsConnected] = useState(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [messages, setMessages] = useState<InterviewMessage[]>([]);
  const [gaStatusMessage, setGaStatusMessage] = useState<string | null>(null);
  const [latestTranscript, setLatestTranscript] = useState<string>('');
  const [latestAssistantMessage, setLatestAssistantMessage] =
    useState<string>('');
  const [latestTranscriptId, setLatestTranscriptId] = useState<string | null>(
    null,
  );
  const [latestAssistantId, setLatestAssistantId] = useState<string | null>(
    null,
  );
  const [isSessionEnded, setIsSessionEnded] = useState<boolean>(false);
  const [endReason, setEndReason] = useState<
    'timeout' | 'wrapup' | 'server' | null
  >(null);
  const [endInterviewButtonHint, setEndInterviewButtonHint] =
    useState<EndInterviewButtonHint | null>(null);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const [totalMs, setTotalMs] = useState<number>(
    options?.fallbackTotalMs ?? TOTAL_TIME_MS,
  );
  const [sessionSegments, setSessionSegments] = useState<InterviewSegment[]>(
    options?.fallbackSegments && options.fallbackSegments.length > 0
      ? options.fallbackSegments
      : INTERVIEW_SEGMENTS,
  );
  const [currentSegment, setCurrentSegment] = useState<InterviewSegment | null>(
    null,
  );
  const [completedSegments, setCompletedSegments] = useState<
    InterviewSegment[]
  >([]);
  const lastUserFinalRef = useRef<number | null>(null);
  const lastAssistantFinalRef = useRef<number | null>(null);
  const [lastUserFinalAt, setLastUserFinalAt] = useState<number | null>(null);
  const [lastAssistantFinalAt, setLastAssistantFinalAt] = useState<
    number | null
  >(null);

  // Keep refs to latest values for immediate access without state delays
  const latestTranscriptRef = useRef<string>('');
  const latestAssistantMessageRef = useRef<string>('');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 8;
  const isConnectingRef = useRef<boolean>(false);
  const connectAbortRef = useRef<AbortController | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track current speaker for auto-flush
  const currentSpeaker = useRef<'user' | 'ai' | null>(null);

  useEffect(() => {
    refreshWsUrlRef.current = options?.refreshWsUrl;
  }, [options?.refreshWsUrl]);

  // Track message history to prevent duplicates
  const messageHistory = useRef<Set<string>>(new Set());

  // Store keepalive intervals for cleanup (WeakMap to avoid type casting)
  const keepaliveIntervals = useRef(
    new WeakMap<WebSocket, ReturnType<typeof setInterval>>(),
  );
  const messageBuffer = useRef<Map<string, InterviewMessage>>(new Map());

  // Deduplicate Whisper packets: interim + final often share the same id
  const seenIds = useRef<Set<string>>(new Set());

  const currentSegmentRef = useRef<InterviewSegment | null>(null);
  const completedSegmentsRef = useRef<Set<InterviewSegment>>(new Set());

  // Track the highest sequence number we've seen
  const highestSequence = useRef<number>(0);

  const normalizeSegment = useCallback(
    (value: unknown): InterviewSegment | null => {
      if (typeof value !== 'string') return null;

      const normalized = value
        .toLowerCase()
        .replace(/[-_]/g, '')
        .replace(/\s+/g, '')
        .trim();

      if (normalized === 'greeting') return 'greeting';
      if (normalized === 'technical') return 'technical';
      if (normalized === 'strategicfit') return 'strategicFit';
      if (normalized === 'wrapup' || normalized === 'wrap') return 'wrapUp';
      if (normalized.startsWith('resume')) return 'resumeWalkthrough';

      return null;
    },
    [],
  );

  const syncCompletedSegments = useCallback(
    (segments: Iterable<InterviewSegment>) => {
      const updated = new Set(completedSegmentsRef.current);
      Array.from(segments).forEach((segment) => {
        if (VALID_SEGMENT_SET.has(segment)) {
          updated.add(segment);
        }
      });
      completedSegmentsRef.current = updated;
      setCompletedSegments(Array.from(updated));
    },
    [],
  );

  const updateCurrentSegment = useCallback(
    (segment: InterviewSegment) => {
      if (!VALID_SEGMENT_SET.has(segment)) return;
      if (currentSegmentRef.current === segment) return;

      currentSegmentRef.current = segment;
      setCurrentSegment(segment);

      const idx = sessionSegments.indexOf(segment);
      if (idx > 0) {
        syncCompletedSegments(sessionSegments.slice(0, idx));
      }
    },
    [sessionSegments, syncCompletedSegments],
  );

  const markSegmentComplete = useCallback(
    (segment: InterviewSegment) => {
      if (!VALID_SEGMENT_SET.has(segment)) return;
      syncCompletedSegments([segment]);
    },
    [syncCompletedSegments],
  );

  // 🎯 P2 FIX: Guard against flushing transcripts that are too short (e.g. filler noise)
  // const MIN_TRANSCRIPT_CHARS = 35; // Increased from 18 to 35 to filter short spurious utterances

  const sendPlaybackDiagnostic = useCallback(
    (diagnostic: PlaybackDiagnostic) => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) return;

      wsRef.current.send(
        JSON.stringify({
          t: 'playback_diagnostic',
          d: diagnostic,
        }),
      );
    },
    [],
  );

  // Initialize audio playback
  const {
    playAudioChunk,
    isAssistantSpeaking: isAudioPlaybackSpeaking,
    initializeAudio,
    stopPlayback,
  } = useAudioPlayback({ onDiagnostic: sendPlaybackDiagnostic });
  const [isAssistantTurnActive, setIsAssistantTurnActive] = useState(false);
  const assistantTurnRef = useRef<{
    turnId: string | null;
    messageId: string | null;
    text: string;
    hasText: boolean;
    audioGateReleased: boolean;
    heldAudioChunks: string[];
    audioGateTimer: ReturnType<typeof setTimeout> | null;
  }>({
    turnId: null,
    messageId: null,
    text: '',
    hasText: false,
    audioGateReleased: false,
    heldAudioChunks: [],
    audioGateTimer: null,
  });

  const releaseHeldAssistantAudio = () => {
    const turn = assistantTurnRef.current;
    if (turn.audioGateTimer) {
      clearTimeout(turn.audioGateTimer);
      turn.audioGateTimer = null;
    }

    const chunks = turn.heldAudioChunks;
    turn.heldAudioChunks = [];
    turn.audioGateReleased = true;
    chunks.forEach((chunk) => playAudioChunk(chunk));
  };

  const resetAssistantTurnRuntime = () => {
    const turn = assistantTurnRef.current;
    if (turn.audioGateTimer) {
      clearTimeout(turn.audioGateTimer);
    }
    assistantTurnRef.current = {
      turnId: null,
      messageId: null,
      text: '',
      hasText: false,
      audioGateReleased: false,
      heldAudioChunks: [],
      audioGateTimer: null,
    };
  };

  const clearAssistantPlaybackRuntime = () => {
    resetAssistantTurnRuntime();
    setIsAssistantTurnActive(false);
  };

  const beginAssistantTurn = (
    turnId: string | null,
    messageId: string | null,
  ) => {
    const turn = assistantTurnRef.current;
    if (turnId && turn.turnId === turnId) {
      if (messageId) turn.messageId = messageId;
      return;
    }

    if (turn.heldAudioChunks.length > 0) {
      releaseHeldAssistantAudio();
    } else if (turn.audioGateTimer) {
      clearTimeout(turn.audioGateTimer);
    }

    assistantTurnRef.current = {
      turnId,
      messageId,
      text: '',
      hasText: false,
      audioGateReleased: false,
      heldAudioChunks: [],
      audioGateTimer: null,
    };
  };
  // Flush current buffer when speaker switches
  const flushCurrentBuffer = () => {
    debugLog(
      '[DEBUG] flushCurrentBuffer called, currentSpeaker:',
      currentSpeaker.current,
    );
    if (currentSpeaker.current === 'user') {
      setLatestTranscript((prev) => {
        if (prev && latestTranscriptId) {
          debugLog('[DEBUG] Flushing user transcript:', prev.substring(0, 50));
          const newMessage: InterviewMessage = {
            type: 'transcript',
            data: prev,
            messageId: latestTranscriptId,
            sequence: highestSequence.current + 1,
            timestamp: Date.now(),
          };

          setTimeout(() => {
            processNewMessage(newMessage);
          }, 300);
        }
        return '';
      });
      setLatestTranscriptId(null);
    } else if (currentSpeaker.current === 'ai') {
      setLatestAssistantMessage((prev) => {
        debugLog(
          '[DEBUG] Flushing assistant message:',
          prev?.substring(0, 50),
          'messageId:',
          latestAssistantId,
        );
        if (prev && latestAssistantId) {
          const newMessage: InterviewMessage = {
            type: 'assistant',
            data: prev,
            messageId: latestAssistantId,
            sequence: highestSequence.current + 1,
            timestamp: Date.now(),
          };

          debugLog(
            '[DEBUG] Created assistant message, calling processNewMessage',
          );
          setTimeout(() => {
            processNewMessage(newMessage);
          }, 300);
        } else {
          debugLog(
            '[DEBUG] Skipping assistant flush - prev or latestAssistantId missing',
          );
        }
        return '';
      });
      setLatestAssistantId(null);
    }
  };

  // Debounced flush to reduce UI churn
  const debouncedFlush = useRef(
    debounce(() => {
      flushCurrentBuffer();
    }, 500),
  );

  // Process new message and add to history
  const processNewMessage = (newMessage: InterviewMessage) => {
    debugLog('[DEBUG] processNewMessage called with:', newMessage);
    if (
      newMessage.messageId &&
      !messageHistory.current.has(newMessage.messageId)
    ) {
      debugLog('[DEBUG] Message is new, adding to history');
      messageHistory.current.add(newMessage.messageId);

      if (newMessage.sequence) {
        highestSequence.current = Math.max(
          highestSequence.current,
          newMessage.sequence,
        );
      }

      setMessages((prev) => {
        const newMessages = [...prev, newMessage];
        debugLog(
          '[DEBUG] Updated messages array, new length:',
          newMessages.length,
        );
        return newMessages;
      });

      if (newMessage.type === 'assistant') {
        const now = Date.now();
        lastAssistantFinalRef.current = now;
        setLastAssistantFinalAt(now);
      }
    } else {
      debugLog('[DEBUG] Message skipped - duplicate or missing messageId');
    }
  };

  const resolveReconnectUrl = async (fallbackUrl: string) => {
    const refreshWsUrl = refreshWsUrlRef.current;
    if (!refreshWsUrl) {
      return fallbackUrl;
    }

    try {
      const freshUrl = await refreshWsUrl();
      return freshUrl || fallbackUrl;
    } catch (error) {
      console.error('Failed to refresh interview connection URL:', error);
      return fallbackUrl;
    }
  };

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const clearConnectingState = (connectAttempt?: AbortController) => {
    if (connectAttempt && connectAbortRef.current !== connectAttempt) {
      return;
    }

    isConnectingRef.current = false;
    getState().connecting = false;
  };

  const isAbortError = (error: unknown) =>
    error instanceof DOMException && error.name === 'AbortError';

  const waitForRetry = (delayMs: number, signal: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Connection attempt cancelled', 'AbortError'));
        return;
      }

      const timeout = window.setTimeout(() => {
        signal.removeEventListener('abort', abort);
        resolve();
      }, delayMs);

      const abort = () => {
        window.clearTimeout(timeout);
        reject(new DOMException('Connection attempt cancelled', 'AbortError'));
      };

      signal.addEventListener('abort', abort, { once: true });
    });

  const closeCurrentSocketForRetry = () => {
    connectAbortRef.current?.abort();
    connectAbortRef.current = null;
    clearReconnectTimer();
    const currentSocket = wsRef.current || getState().ws;

    if (currentSocket) {
      if (wsRef.current === currentSocket) {
        wsRef.current = null;
      }
      if (getState().ws === currentSocket) {
        getState().ws = null;
      }

      try {
        if (
          currentSocket.readyState === WebSocket.OPEN ||
          currentSocket.readyState === WebSocket.CONNECTING
        ) {
          currentSocket.close(1000, 'client-reconnect');
        }
      } catch {
        // ignore - retry will open a new socket
      }
    }

    clearConnectingState();
    setIsConnected(false);
    setIsReady(false);
    clearAssistantPlaybackRuntime();
  };

  // Connect to WebSocket
  const connect = async (url: string) => {
    if (!url) {
      return;
    }

    // Clear any buffered audio from a previous session before attaching to a new WS
    stopPlayback();
    clearAssistantPlaybackRuntime();
    setIsSessionEnded(false);
    setEndReason(null);
    setEndInterviewButtonHint(null);

    // Prevent parallel connection attempts (hook-level and tab-wide)
    if (isConnectingRef.current || getState().connecting) return;
    if (
      (wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING)) ||
      (getState().ws &&
        (getState().ws!.readyState === WebSocket.OPEN ||
          getState().ws!.readyState === WebSocket.CONNECTING))
    ) {
      if (!wsRef.current && getState().ws) {
        wsRef.current = getState().ws;
      }
      return;
    }

    const connectAbort = new AbortController();
    connectAbortRef.current = connectAbort;

    try {
      isConnectingRef.current = true;
      getState().connecting = true;
      setConnectionError(null);
      setGaStatusMessage('Preparing Alex\u2019s interview room\u2026');
      const urlObj = new URL(url);
      const isEnterpriseSession =
        urlObj.searchParams.get('sessionMode') === 'enterprise';
      const existingTicket = urlObj.searchParams.get('ticket');

      const getAdmitTicketForWsUrl = async (): Promise<string> => {
        // Always use API Gateway for /admit so it's available even if ECS has 0 healthy targets
        const apiBase = ADMIT_API_BASE;
        const startedAt = Date.now();

        const attempt = async (n: number): Promise<string> => {
          let res: Response;
          try {
            res = await fetch(`${apiBase}/admit`, {
              method: 'POST',
              credentials: 'include',
              signal: connectAbort.signal,
            });
          } catch (error) {
            if (isAbortError(error)) {
              throw error;
            }

            if (Date.now() - startedAt >= ADMIT_RETRY_TIMEOUT_MS) {
              throw error;
            }

            const backoffMs = Math.min(
              ADMIT_RETRY_MAX_DELAY_MS,
              Math.floor(500 * 1.35 ** n),
            );
            setGaStatusMessage('Still preparing Alex\u2019s interview room\u2026');
            await waitForRetry(backoffMs, connectAbort.signal);
            return attempt(n + 1);
          }

          if (res.ok) {
            let body: { ticket?: string } = {};
            try {
              body = await res.json();
            } catch {
              // ignore
            }
            if (body.ticket) return body.ticket;
            throw new Error('Admission succeeded but no ticket returned');
          }

          if (
            res.status === 409 &&
            Date.now() - startedAt < ADMIT_RETRY_TIMEOUT_MS
          ) {
            const backoffMs = Math.min(
              ADMIT_RETRY_MAX_DELAY_MS,
              Math.floor(500 * 1.35 ** n),
            );
            setGaStatusMessage('Still preparing Alex\u2019s interview room\u2026');
            await waitForRetry(backoffMs, connectAbort.signal);
            return attempt(n + 1);
          }

          if (res.status === 409) {
            throw new Error(
              'Alex is taking longer than expected to connect. Please try reconnecting.',
            );
          }

          {
            let msg = '';
            try {
              msg = await res.text();
            } catch {
              // ignore
            }
            throw new Error(`Start failed: ${res.status} ${msg}`);
          }
        };

        return attempt(1);
      };

      if (existingTicket) {
        // Enterprise entry/join should arrive with a backend-issued ticketed URL.
      } else if (isEnterpriseSession) {
        throw new Error(
          'This enterprise interview is missing a valid session ticket. Please reopen the invite link and continue from setup again.',
        );
      } else {
        const ticket = await getAdmitTicketForWsUrl();
        urlObj.searchParams.set('ticket', ticket);
      }

      if (connectAbort.signal.aborted) {
        throw new DOMException('Connection attempt cancelled', 'AbortError');
      }

      // Get auth token for WebSocket authentication
      let authToken: string | null = null;
      try {
        authToken = await getToken();
      } catch (error) {
        console.error('Failed to get auth token for WebSocket:', {
          error: String(error),
        });
      }

      // For maximum compatibility through ALB/Proxies, pass auth as a query param
      // rather than Sec-WebSocket-Protocol. The server will normalize to Bearer.
      if (authToken) {
        urlObj.searchParams.set('auth', authToken);
      }
      const finalUrl = urlObj.toString();

      // Create WebSocket (no custom protocol header required)
      if (connectAbort.signal.aborted) {
        throw new DOMException('Connection attempt cancelled', 'AbortError');
      }
      const ws = new WebSocket(finalUrl);
      wsRef.current = ws;
      getState().ws = ws;

      ws.onopen = () => {
        if (connectAbort.signal.aborted) {
          ws.close(1000, 'client-disconnected');
          return;
        }

        setIsConnected(true);
        setIsReady(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;

        // Send initial connection message
        const connectionMessage: InterviewMessage = {
          type: 'connection',
          data: 'Connected to interview session',
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, connectionMessage]);
        isConnectingRef.current = false;
        getState().connecting = false;
        setGaStatusMessage('Preparing Alex\u2019s audio\u2026');

        // Start keepalive ping every 25s to prevent ALB idle timeout
        const keepaliveInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ t: 'ping', d: 'keepalive' }));
            } catch (e) {
              console.error('Failed to send keepalive ping', e);
            }
          } else {
            clearInterval(keepaliveInterval);
          }
        }, 25 * 1000); // 25 seconds

        // Store interval for cleanup (using WeakMap to avoid type casting)
        keepaliveIntervals.current.set(ws, keepaliveInterval);
      };

      ws.onmessage = (event) => {
        if (connectAbort.signal.aborted) {
          return;
        }

        try {
          const data = JSON.parse(event.data);

          // Handle ai-interview-web-app format (t/d structure)
          if (data.t) {
            switch (data.t) {
              case 'transcript':
                // Only route user deltas into the user buffer; assistant text arrives via 'assistant'
                if (data.role === 'user') {
                  handleTranscriptMessage({
                    type: 'transcript',
                    content: data.d,
                    messageId: data.messageId,
                    sequence: data.sequence,
                    partial: data.partial,
                  });
                }
                break;
              case 'assistant':
                handleAssistantMessage({
                  type: 'assistant',
                  content: data.d,
                  messageId: data.messageId,
                  sequence: data.sequence,
                  partial: data.partial,
                  delta: data.delta,
                });
                break;
              case 'assistant_transcript':
                // Intentionally ignored: assistant text is the only transcript source.
                break;
              case 'user_final': {
                // Finalize the user's utterance; advance UI off this event
                debugLog('[DEBUG] Received user_final:', data.d);
                const now = Date.now();
                lastUserFinalRef.current = now;
                setLastUserFinalAt(now);
                const finalMsg: InterviewMessage = {
                  type: 'transcript',
                  data: data.d || '',
                  messageId: `user_final-${Date.now()}`,
                  sequence: highestSequence.current + 1,
                  timestamp: Date.now(),
                };
                debugLog('[DEBUG] Created finalMsg:', finalMsg);
                processNewMessage(finalMsg);
                debugLog(
                  '[DEBUG] After processNewMessage, messages count:',
                  messages.length,
                );
                // Clear live transcript buffer so UI stops showing interim text
                setLatestTranscript('');
                setLatestTranscriptId(null);
                latestTranscriptRef.current = '';
                currentSpeaker.current = null;
                break;
              }
              case 'audio':
                handleAudioChunk({
                  type: 'audio_chunk',
                  audio: data.d,
                });
                break;
              case 'error':
                handleErrorMessage({
                  type: 'error',
                  message: data.d,
                });
                break;
              case 'session_end': {
                try {
                  wsRef.current?.send(JSON.stringify({ t: 'session_end_ack' }));
                } catch (error) {
                  console.error(error);
                }
                // Only auto-end/redirect on hard timeout. Otherwise, stay in-session until user clicks End Interview.
                const reason = data?.d?.reason;
                const isTimeout = reason === 'time-limit';
                const isWrapup = reason === 'wrapup';

                if (isTimeout) {
                  setRemainingMs(0); // Hard stop for actual timeout
                  setIsSessionEnded(true);
                  setEndReason('timeout');
                  break;
                }

                if (isWrapup) {
                  setEndReason('wrapup');
                  setIsSessionEnded(false);
                  setEndInterviewButtonHint({
                    target: 'end_interview_button',
                    action: 'highlight',
                    reason: 'wrapup',
                    durationMs: 12_000,
                    message:
                      'Alex has wrapped up. Use the highlighted End interview button when you are ready.',
                    nonce: Date.now(),
                  });
                  break;
                }

                setIsSessionEnded(false);
                setEndReason((prev) => prev ?? 'server');
                break;
              }
              case 'interview_ui_hint': {
                const hint = data?.d;
                if (
                  hint?.target === 'end_interview_button' &&
                  hint?.action === 'highlight'
                ) {
                  setEndInterviewButtonHint({
                    target: 'end_interview_button',
                    action: 'highlight',
                    reason:
                      typeof hint.reason === 'string' ? hint.reason : undefined,
                    durationMs:
                      typeof hint.durationMs === 'number'
                        ? hint.durationMs
                        : undefined,
                    message:
                      typeof hint.message === 'string' ? hint.message : undefined,
                    nonce: Date.now(),
                  });
                }
                break;
              }
              case 'timer':
                // Keep it monotonic - sometimes messages can arrive out of order
                if (typeof data.d?.remainingMs === 'number') {
                  setRemainingMs((prev) =>
                    prev === null
                      ? data.d.remainingMs
                      : Math.min(prev, data.d.remainingMs),
                  );
                }
                break;
              case 'status': {
                const payload = data.d;
                if (
                  payload &&
                  typeof payload === 'object' &&
                  'event' in payload &&
                  typeof payload.event === 'string'
                ) {
                  debugLog('[DEBUG] Status event received:', payload.event);
                }
                const flags =
                  payload && typeof payload === 'object' ? payload : {};
                const statusEvent =
                  payload && typeof payload === 'object' && 'event' in payload
                    ? (payload as Record<string, unknown>).event
                    : null;
                if (typeof statusEvent === 'string') {
                  if (statusEvent === 'ga_initializing') {
                    setGaStatusMessage('Preparing Alex\u2019s audio\u2026');
                  } else if (statusEvent === 'assistant_turn_complete') {
                    const now = Date.now();
                    lastAssistantFinalRef.current = now;
                    setLastAssistantFinalAt(now);
                  } else if (statusEvent === 'segment_progress') {
                    // no-op
                  } else if (!flags.connected) {
                    setGaStatusMessage('Connecting to Alex\u2026');
                  }
                } else if (flags.connected === false) {
                  setGaStatusMessage('Connecting to Alex\u2026');
                }
                if (typeof flags.connected === 'boolean') {
                  setIsReady(flags.connected);
                  if (flags.connected) {
                    setGaStatusMessage(null);
                  }
                }
                if (payload && typeof payload === 'object') {
                  const recordPayload = payload as Record<string, unknown>;
                  const segmentValue =
                    recordPayload.segment ??
                    recordPayload.phase ??
                    recordPayload.step ??
                    recordPayload.stage ??
                    recordPayload.currentSegment;

                  const normalizedSegment = normalizeSegment(segmentValue);
                  if (normalizedSegment) {
                    updateCurrentSegment(normalizedSegment);
                  }

                  if (
                    statusEvent === 'session_config' &&
                    typeof recordPayload.totalMs === 'number' &&
                    recordPayload.totalMs > 0
                  ) {
                    setTotalMs(recordPayload.totalMs);
                  }

                  if (
                    statusEvent === 'session_config' &&
                    Array.isArray(recordPayload.segments)
                  ) {
                    const normalizedSegments = (
                      recordPayload.segments as unknown[]
                    )
                      .map((value) => normalizeSegment(value))
                      .filter(Boolean) as InterviewSegment[];
                    if (normalizedSegments.length > 0) {
                      setSessionSegments(normalizedSegments);
                    }
                  }

                  const completedValue =
                    recordPayload.completedSegment ??
                    recordPayload.lastCompletedSegment ??
                    recordPayload.segmentCompleted;
                  const normalizedCompleted = normalizeSegment(completedValue);
                  if (normalizedCompleted) {
                    markSegmentComplete(normalizedCompleted);
                  }

                  if (Array.isArray(recordPayload.completedSegments)) {
                    const normalizedList = recordPayload.completedSegments
                      .map((value) => normalizeSegment(value))
                      .filter(Boolean) as InterviewSegment[];

                    if (normalizedList.length > 0) {
                      syncCompletedSegments(normalizedList);
                    }
                  }

                  if (
                    typeof recordPayload.segmentIndex === 'number' &&
                    recordPayload.segmentIndex >= 0 &&
                    recordPayload.segmentIndex < sessionSegments.length
                  ) {
                    updateCurrentSegment(
                      sessionSegments[Math.floor(recordPayload.segmentIndex)],
                    );
                  }
                }
                // Mic half-duplex: mute while AI is speaking, unmute on stop
                if (typeof flags.speaking === 'boolean') {
                  setIsAssistantTurnActive(flags.speaking);
                  debugLog(
                    '[DEBUG] Speaking status:',
                    flags.speaking,
                    'currentSpeaker:',
                    currentSpeaker.current,
                    'latestAssistant:',
                    latestAssistantMessageRef.current?.substring(0, 50),
                  );
                  window.dispatchEvent(
                    new CustomEvent('interview-mic-toggle', {
                      detail: { enabled: !flags.speaking },
                    }),
                  );
                  if (
                    flags.speaking === false &&
                    currentSpeaker.current === 'ai' &&
                    latestAssistantMessageRef.current
                  ) {
                    debugLog(
                      '[DEBUG] Calling debouncedFlush for assistant message',
                    );
                    debouncedFlush.current();
                  }
                  if (flags.speaking === false) {
                    releaseHeldAssistantAudio();
                    resetAssistantTurnRuntime();
                  }
                }
                break;
              }
              case 'connection_status': {
                // 🚨 ENHANCED CONNECTION STATUS: Handle all connection states with user notifications
                const status = data.d?.status;

                switch (status) {
                  case 'reconnecting':
                    setIsReady(false);
                    break;
                  case 'reconnected':
                  case 'connected':
                    setIsReady(true);
                    break;
                  case 'processing_issue':
                    // Show processing indicator but don't disable interface completely
                    break;
                  case 'recovered':
                    // Processing recovered, everything back to normal
                    setIsReady(true);
                    break;
                  case 'processed':
                    // Successful processing - can show brief confirmation
                    break;
                  case 'error':
                    // Show error but allow retry
                    // Auto-recover after 3 seconds
                    setTimeout(() => {
                      setIsReady(true);
                    }, 3000);
                    break;
                  case 'disconnected':
                  case 'failed':
                    setIsReady(false);
                    break;
                  default:
                    break;
                }
                break;
              }
              case 'auth_refresh_request': {
                (async () => {
                  try {
                    const freshToken = await getToken();
                    if (freshToken && ws.readyState === WebSocket.OPEN) {
                      ws.send(
                        JSON.stringify({
                          t: 'update_auth',
                          d: freshToken,
                        }),
                      );
                    }
                  } catch (err) {
                    console.error(
                      'Failed to refresh interview auth token',
                      err,
                    );
                  }
                })();
                break;
              }
              default:
                break;
            }
          } else {
            // Handle wisedoc format (type structure)
            switch (data.type) {
              case 'transcript':
                handleTranscriptMessage(data);
                break;
              case 'assistant_response':
                handleAssistantMessage({
                  ...data,
                  type: 'assistant',
                });
                break;
              case 'audio_chunk':
                handleAudioChunk(data);
                break;
              case 'question':
                handleQuestionMessage(data);
                break;
              case 'error':
                handleErrorMessage(data);
                break;
              default:
                break;
            }
          }
        } catch (error) {
          console.error(error);
        }
      };

      ws.onerror = (error) => {
        if (connectAbort.signal.aborted) {
          return;
        }

        console.error(error);
        setGaStatusMessage('Preparing Alex\u2019s interview room\u2026');
        clearConnectingState(connectAbort);
      };

      ws.onclose = (event) => {
        // Clear keepalive interval
        const keepaliveInterval = keepaliveIntervals.current.get(ws);
        if (keepaliveInterval) {
          clearInterval(keepaliveInterval);
          keepaliveIntervals.current.delete(ws);
        }

        const isCurrentSocket = wsRef.current === ws || getState().ws === ws;
        if (!isCurrentSocket) {
          return;
        }

        if (connectAbort.signal.aborted) {
          setIsConnected(false);
          setIsReady(false);
          wsRef.current = null;
          getState().ws = null;
          clearConnectingState();
          clearAssistantPlaybackRuntime();
          return;
        }

        if (event.reason === 'time-limit') {
          setIsSessionEnded(true);
          setEndReason('timeout');
        } else if (event.code === 1000) {
          setIsSessionEnded(true);
          setEndReason((prev) => prev ?? 'server');
        }

        setIsConnected(false);
        setIsReady(false);
        wsRef.current = null;
        clearConnectingState(connectAbort);
        getState().ws = null;
        clearAssistantPlaybackRuntime();

        // Attempt reconnection if not a clean close
        if (
          event.code !== 1000 &&
          reconnectAttempts.current < maxReconnectAttempts
        ) {
          setConnectionError(null);
          setGaStatusMessage('Still preparing Alex\u2019s interview room\u2026');
          clearReconnectTimer();
          reconnectTimerRef.current = setTimeout(async () => {
            reconnectTimerRef.current = null;
            if (connectAbort.signal.aborted) {
              return;
            }

            reconnectAttempts.current += 1;

            const nextUrl = await resolveReconnectUrl(url);
            if (connectAbort.signal.aborted) {
              return;
            }

            connect(nextUrl);
          }, Math.min(5000, 1000 + 500 * reconnectAttempts.current));
        } else if (reconnectAttempts.current >= maxReconnectAttempts) {
          setConnectionError(
            'Alex is taking longer than expected to connect. Please try reconnecting.',
          );
        }
      };
    } catch (error) {
      if (isAbortError(error)) {
        clearConnectingState(connectAbort);
        return;
      }

      console.error(error);
      setConnectionError(
        error instanceof Error
          ? error.message
          : 'Failed to connect to interview session',
      );
      clearConnectingState(connectAbort);
    }
  };

  // Handle different message types
  const handleTranscriptMessage = (data: WebSocketMessageData) => {
    const pktId = data.messageId || data.id || 'unknown';
    if (seenIds.current.has(pktId)) {
      return; // ← DROP the duplicate
    }
    seenIds.current.add(pktId);

    if (currentSpeaker.current !== 'user') {
      flushCurrentBuffer();
      currentSpeaker.current = 'user';
    }

    setLatestTranscript(data.content || data.data || '');

    // --- Debug bundle: ASR packet ID log ---
    // Format: ts | transcript_id | is_final
    // let isFinal = 1; // default assume final
    // if (data.partial !== undefined) {
    //   isFinal = data.partial ? 0 : 1;
    // }
    // const packetId = data.messageId || data.id || 'unknown';
    setLatestTranscriptId(data.messageId || data.id || null);

    latestTranscriptRef.current = data.content || data.data || '';
    // Do not finalize here; final user message comes via 'user_final'
  };

  const handleAssistantMessage = (data: WebSocketMessageData) => {
    debugLog('[DEBUG] handleAssistantMessage called, data:', data);
    if (currentSpeaker.current !== 'ai') {
      debugLog(
        '[DEBUG] Speaker changed from',
        currentSpeaker.current,
        'to ai, flushing',
      );
      flushCurrentBuffer();
      currentSpeaker.current = 'ai';
    }

    const assistantText = data.content || data.data || '';
    const currentTurn = assistantTurnRef.current;
    const turnId =
      data.turnId ||
      (data.partial ? currentTurn.turnId : null) ||
      data.messageId ||
      data.id ||
      `assistant-turn-${Date.now()}`;
    const messageId =
      data.messageId || data.id || currentTurn.messageId || turnId;
    beginAssistantTurn(turnId, messageId);
    const activeTurn = assistantTurnRef.current;
    const isDelta = data.partial === true && data.delta !== false;
    const nextAssistantText = isDelta
      ? `${activeTurn.text}${assistantText}`
      : assistantText;
    activeTurn.text = nextAssistantText;
    activeTurn.hasText = nextAssistantText.trim().length > 0;
    debugLog(
      '[DEBUG] Setting assistant message:',
      nextAssistantText?.substring(0, 50),
      'partial:',
      data.partial,
      'messageId:',
      messageId,
    );
    setLatestAssistantMessage(nextAssistantText);
    setLatestAssistantId(messageId);
    latestAssistantMessageRef.current = nextAssistantText;
    if (activeTurn.hasText) {
      releaseHeldAssistantAudio();
    }

    // If message is final (partial: false), finalize it immediately instead of waiting for speaking:false
    if (data.partial === false && nextAssistantText && messageId) {
      debugLog(
        '[DEBUG] Message is final (partial:false), finalizing immediately with messageId:',
        messageId,
      );

      // Create and process the message directly to avoid async state issues
      const finalMessage: InterviewMessage = {
        type: 'assistant',
        data: nextAssistantText,
        messageId,
        sequence: highestSequence.current + 1,
        timestamp: Date.now(),
      };

      setTimeout(() => {
        processNewMessage(finalMessage);
        // Clear the buffer after processing
        setLatestAssistantMessage('');
        setLatestAssistantId(null);
        latestAssistantMessageRef.current = '';
        if (assistantTurnRef.current.turnId === turnId) {
          resetAssistantTurnRuntime();
        }
      }, 100);
    }
    // Otherwise, finalize when server signals speaking:false via 'status'
  };

  const handleAudioChunk = (data: WebSocketMessageData) => {
    if (data.audio) {
      debugLog('[DEBUG] Received audio chunk length:', data.audio.length);
      const currentTurn = assistantTurnRef.current;
      const turnId =
        data.turnId ||
        currentTurn.turnId ||
        data.messageId ||
        `assistant-audio-${Date.now()}`;
      const messageId = data.messageId || currentTurn.messageId || turnId;
      beginAssistantTurn(turnId, messageId);

      const activeTurn = assistantTurnRef.current;
      if (activeTurn.hasText || activeTurn.audioGateReleased) {
        activeTurn.audioGateReleased = true;
        playAudioChunk(data.audio);
        return;
      }

      activeTurn.heldAudioChunks.push(data.audio);
      if (!activeTurn.audioGateTimer) {
        activeTurn.audioGateTimer = setTimeout(
          releaseHeldAssistantAudio,
          FIRST_ASSISTANT_AUDIO_SYNC_GATE_MS,
        );
      }
    }
  };

  const handleQuestionMessage = (data: WebSocketMessageData) => {
    const questionMessage: InterviewMessage = {
      type: 'question',
      data: data.content || data.data || '',
      messageId: data.messageId || data.id,
      sequence: data.sequence || highestSequence.current + 1,
      timestamp: Date.now(),
    };
    processNewMessage(questionMessage);
  };

  const handleErrorMessage = (data: WebSocketMessageData) => {
    // 🛡️ PHANTOM PREVENTION: Handle phantom feedback loop errors with audio restart
    if (
      data.type === 'phantom_feedback_loop' &&
      data.action === 'restart_audio'
    ) {
      // Emit event for microphone restart
      window.dispatchEvent(
        new CustomEvent('phantom-restart-audio', {
          detail: {
            message: data.message,
            consecutivePhantoms: data.consecutivePhantoms,
          },
        }),
      );

      const phantomMessage: InterviewMessage = {
        type: 'error',
        data:
          data.message || 'Audio feedback detected. Restarting microphone...',
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, phantomMessage]);
      return;
    }

    const errorMessage: InterviewMessage = {
      type: 'error',
      data: data.message || data.data || 'An error occurred',
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, errorMessage]);
  };

  // Send message through WebSocket
  const sendMessage = (message: SendMessageData) => {
    if (wsRef.current && isConnected) {
      wsRef.current.send(JSON.stringify(message));
    }
  };

  // Send text message
  const sendTextMessage = (text: string) => {
    const trimmedText = text.trim();
    if (!trimmedText || wsRef.current?.readyState !== WebSocket.OPEN) {
      return false;
    }

    // Use AI Interview WebSocket protocol format
    const message = {
      t: 'uTxt',
      d: trimmedText,
    };

    wsRef.current.send(JSON.stringify(message));

    // Add to messages immediately for UI feedback
    const textMessage: InterviewMessage = {
      type: 'transcript',
      data: trimmedText,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, textMessage]);

    return true;
  };

  // Send audio data
  const sendAudioData = (audioData: ArrayBuffer) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      // Convert ArrayBuffer to base64 for AI Interview format
      const uint8Array = new Uint8Array(audioData);
      const base64Audio = btoa(String.fromCharCode(...uint8Array));

      // Use AI Interview WebSocket protocol format
      const message = {
        t: 'uAud',
        d: base64Audio,
      };

      wsRef.current.send(JSON.stringify(message));
    }
  };

  // Disconnect WebSocket
  const disconnect = () => {
    connectAbortRef.current?.abort();
    connectAbortRef.current = null;
    clearReconnectTimer();
    const currentSocket = wsRef.current || getState().ws;

    if (currentSocket) {
      currentSocket.close(1000, 'User disconnected');
    }

    wsRef.current = null;
    getState().ws = null;
    clearConnectingState();
    setIsConnected(false);
    setIsReady(false);
    stopPlayback();
    clearAssistantPlaybackRuntime();
  };

  const retryConnection = () => {
    if (!wsUrl) {
      return;
    }
    closeCurrentSocketForRetry();
    reconnectAttempts.current = 0;
    setConnectionError(null);
    setGaStatusMessage('Preparing Alex\u2019s interview room\u2026');
    const retryAbort = new AbortController();
    connectAbortRef.current = retryAbort;
    resolveReconnectUrl(wsUrl)
      .then((nextUrl) => {
        if (retryAbort.signal.aborted) {
          return;
        }

        connect(nextUrl);
      })
      .catch((error) => {
        if (retryAbort.signal.aborted || isAbortError(error)) {
          return;
        }

        console.error('Failed to reconnect to WebSocket:', error);
        setConnectionError(
          'Alex is taking longer than expected to connect. Please try reconnecting.',
        );
      });
  };

  // Start interview session
  const startInterview = () => {
    sendMessage({
      t: 'start_interview',
      d: null,
      timestamp: Date.now(),
    });
  };

  // End interview session
  const endInterview = () => {
    flushCurrentBuffer();
    stopPlayback();
    clearAssistantPlaybackRuntime();
    sendMessage({
      t: 'end_interview',
      d: null, // Always include d field to prevent logging errors
      timestamp: Date.now(),
    });
  };

  // Clear messages
  const clearMessages = () => {
    setMessages([]);
    setLatestTranscript('');
    setLatestAssistantMessage('');
    messageHistory.current.clear();
    messageBuffer.current.clear();
    highestSequence.current = 0;
    lastAssistantFinalRef.current = null;
    lastUserFinalRef.current = null;
    setLastAssistantFinalAt(null);
    setLastUserFinalAt(null);
    setEndReason(null);
    setEndInterviewButtonHint(null);
    clearAssistantPlaybackRuntime();
  };

  const injectAssistantMessage = (text: string) => {
    if (!text.trim()) {
      return;
    }

    const now = Date.now();
    const message: InterviewMessage = {
      type: 'assistant',
      data: text.trim(),
      messageId: `client-assistant-${now}`,
      sequence: highestSequence.current + 1,
      timestamp: now,
    };

    processNewMessage(message);
    lastAssistantFinalRef.current = now;
    setLastAssistantFinalAt(now);
  };

  // Auto-connect if URL is provided
  useEffect(() => {
    if (wsUrl) {
      connect(wsUrl).catch((error) => {
        console.error('Failed to connect to WebSocket:', error);
      });
    }

    return () => {
      disconnect();
    };
  }, [wsUrl]);

  return {
    // Connection state
    isConnected,
    isReady,
    connectionError,

    // Messages
    messages,
    latestTranscript,
    latestAssistantMessage,

    // Audio playback state
    isAssistantSpeaking: isAudioPlaybackSpeaking || isAssistantTurnActive,
    initializeAudio, // 🔧 O3 P0 FIX: Export initializeAudio to avoid duplicate hook instances

    // Session state
    isSessionEnded,
    endReason,
    endInterviewButtonHint,
    remainingMs,
    totalMs,
    sessionSegments,

    // Actions
    connect,
    retryConnection,
    disconnect,
    sendMessage,
    sendTextMessage,
    sendAudioData,
    startInterview,
    endInterview,
    clearMessages,
    injectAssistantMessage,

    // Utility
    getCurrentTranscript: () => latestTranscriptRef.current,
    getCurrentAssistantMessage: () => latestAssistantMessageRef.current,
    segmentProgress: {
      current: currentSegment,
      completed: completedSegments,
    },
    gaStatusMessage,
    lastUserFinalAt,
    lastAssistantFinalAt,
  };
};
